import type { PaginationResult } from "@/domains/table-processor"
import { ChevronRightIcon } from "@/domains/icons"
import { ChevronLeftIcon } from "@/domains/icons/huge-icons/monotone/chevron-left"
import { cn } from "@/domains/library"
import useResponsive from "@/hooks/use-responsive"

type Props = {
  paginated: PaginationResult
  rowsPerPage?: Array<number>
  onChangeRowsPerPage: (rowsPerPage: number) => void
  onClickPreviousPage: (previousPageNumber: number) => void
  onClickNextPage: (nextPageNumber: number) => void
}

const ROWS_PER_PAGE: Array<number> = [10, 25, 50, 100, 250]

export const Pagination = ({
  paginated,
  onChangeRowsPerPage,
  onClickPreviousPage,
  onClickNextPage,
  rowsPerPage = ROWS_PER_PAGE,
}: Props) => {
  const { isMobile } = useResponsive()

  return (
    <div
      className={cn(
        "flex w-full items-center py-4",
        isMobile ? "justify-center gap-2 text-xs" : "justify-end gap-3 text-sm",
      )}
    >
      <div className="text-t300">Rows per page:</div>
      <div>
        <select
          className="rounded-lg border border-p400-light bg-p400 p-1 text-t100 ring-0"
          onChange={(event) => {
            onChangeRowsPerPage(Number(event.target.value))
          }}
          defaultValue={paginated.itemsPerPage}
        >
          {rowsPerPage.map((rowsPerPage) => (
            <option key={rowsPerPage} value={rowsPerPage}>
              {rowsPerPage}
            </option>
          ))}
        </select>
      </div>
      <p className="text-t300">{paginated?.pageInfo}</p>
      <div className={cn("flex h-fit flex-row items-center ", isMobile ? "gap-1" : "gap-1")}>
        <PageButtonIcon
          canProgress={paginated.hasPreviousPage}
          disabled={paginated.hasPreviousPage !== true}
          onClick={() => {
            onClickPreviousPage(paginated.previousPageNumber)
          }}
        >
          <ChevronLeftIcon size={20} />
        </PageButtonIcon>

        <PageButtonIcon
          canProgress={paginated.hasNextPage}
          disabled={paginated.hasNextPage !== true}
          onClick={() => {
            onClickPreviousPage(paginated.nextPageNumber)
          }}
        >
          <ChevronRightIcon size={20} />
        </PageButtonIcon>
      </div>
    </div>
  )
}

const PageButtonIcon = ({
  canProgress,
  children,
  disabled,
  onClick,
}: {
  canProgress: boolean
  children: React.ReactNode
  disabled: boolean
  onClick: () => void
}) => {
  const { isTablet, isDesktop } = useResponsive()
  return (
    <button
      className={cn(
        "flex items-center justify-center",
        "bg-p500 hover:brightness-125 disabled:bg-p500 disabled:hover:brightness-100",
        "hover-text-t100 disabled:text-neutral-700 text-t100",
        "border-neutral-700 border",
        "rounded-lg",
        "transition-colors duration-300 ease-in-out",
        canProgress ? "hover:cursor-pointer" : "cursor-not-allowed",
        isTablet || isDesktop ? "h-8 w-8" : "h-5 w-5",
      )}
      disabled={disabled}
      onClick={() => {
        onClick()
      }}
    >
      {children}
    </button>
  )
}
